import { useContext, type ReactNode } from "react";
import { Navigate } from "react-router-dom";

import AppContext from "../context/AppContext";
import FeatureFlagGuard from "../components/FeatureFlagGuard/FeatureFlagGuard";
import RequireAuth from "../RequireAuth";
import { isDevModeEnv } from "../hooks/useIsDevMode";
import { NAVIGATION_ROUTES } from "../utils/NavigationUtils";
import { TRIVELTA_PRODUCTS } from "../utils/products";
import { isEntryProductReachable } from "./guardTriage";
import ProductFeatureFlagGuard from "./ProductFeatureFlagGuard";
import { findFirstMatchingRoute } from "./redirectPriority";

export type ProtectedElementOptions = {
  /** Permission key(s) required to access the route. When an array is passed
   *  the check is OR. */
  requiredPermission?: string | string[];
  /** Work-in-progress routes: only reachable in dev/test environments
   *  (`FEATURE_GUARD_ALLOWED_ENVIRONMENTS`). Permission check is skipped
   *  in those environments. */
  developmentMode?: boolean;
  /** When set, wraps the page in route-mode `FeatureFlagGuard` so direct
   *  navigation is blocked when the flag is off (maps via `featureFlagConfig`).
   *  Example: `"gamePass"` → `additional_features.game_pass`. For Game Pass,
   *  `allowGamePassPamAccess` bypasses only the GA Seasons permission. */
  featureFlag?: string;
  /** Trivelta Product scope_key (TCM V2). When set, the route is only reachable
   *  when the product is active for the client per `/admin-panel/platform/v2/config`.
   *  Combined with `requiredPermission` as an AND — product + role-permission both
   *  required. See `src/utils/products.ts` for the canonical keys. */
  requiredProduct?: string;
  /** When true, `requiredProduct` is resolved against TCM V2 catalog presence
   *  only. This avoids `isProductActive`'s non-dev fail-open behavior during
   *  the PAMENG-1820 rollout for products that were never v1-flag-gated. */
  requiredProductCatalogOnly?: boolean;
  /** Legacy feature flag to consult in non-dev envs while the v2 product
   *  catalog is rolling out. Forwarded to `<FeatureFlagGuard>` as the
   *  `feature` prop and consumed as the fallback signal — see the JSDoc on
   *  `FeatureFlagGuard.js` and `FeatureFlagService.isProductActive` for the
   *  full rollout semantics + the release checklist.
   *
   *  TODO(PAMENG-1820): remove this field and every callsite that passes it
   *  (Game Pass routes today) once BE exposes every product in every env. */
  requiredProductFallbackFeature?: string;
  /**
   * TCM V2 `products.*.feature_flags` entry (e.g. `transactions_routing`).
   * Distinct from `featureFlag` (v1 `features_status_config`). Wraps the page
   * in route-mode `ProductFeatureFlagGuard`.
   */
  requiredProductFeatureFlag?: string;
  /**
   * Required state of `requiredProductFeatureFlag`. Defaults to `true` (flag
   * must be on). Pass `false` for legacy routes that should only be reachable
   * while the flag is off (Withdrawal Review V1).
   */
  requireProductFeatureFlagEnabled?: boolean;
  /** Sibling redirect when the product-feature-flag gate fails. */
  productFeatureFlagRedirectTo?: string;
};

type AppContextShape = {
  adminPermissions?: string[] | null;
  adminDataLoaded?: boolean;
};

/**
 * Renders the redirect target when an admin hits a `developmentMode: true`
 * route from a non-dev/test env (i.e. prod/stg). Walks `REDIRECT_PRIORITY`
 * to land them on the same Sider tab `RedirectIfAuthenticated` would have
 * picked at login, so we don't bounce them off `/users/home` when they
 * lack the `users` permission.
 *
 * Falls back to `/users/home` if the admin profile hasn't loaded yet or
 * holds no permission recognised by the Sider — that preserves the
 * pre-refactor behavior for the rare hard-reload edge case and lets
 * `RequireAuth` on `/users/home` handle the no-permission terminal state.
 */
const DevModeRedirect = (): ReactNode => {
  const ctx = (useContext(AppContext) ?? {}) as AppContextShape;
  // Reachable-first, then unrestricted, then the historical /users/home edge.
  const target =
    findFirstMatchingRoute(ctx.adminPermissions ?? null, {
      isEntryReachable: isEntryProductReachable,
    }) ??
    findFirstMatchingRoute(ctx.adminPermissions ?? null) ??
    NAVIGATION_ROUTES.USERS;
  return <Navigate to={target} replace />;
};

/**
 * Wraps a route page in `<RequireAuth>` and optional dev / feature-flag gates.
 * Imported by `App.js` so the route table stays readable while feature-flag
 * routing logic stays reusable and out of the App.js ESLint override.
 */
export function protectedElement(
  children: ReactNode,
  {
    requiredPermission,
    developmentMode = false,
    featureFlag,
    requiredProduct,
    requiredProductCatalogOnly = false,
    requiredProductFallbackFeature,
    requiredProductFeatureFlag,
    requireProductFeatureFlagEnabled = true,
    productFeatureFlagRedirectTo,
  }: ProtectedElementOptions = {},
): ReactNode {
  if (developmentMode && !isDevModeEnv()) {
    return <DevModeRedirect />;
  }

  let content = children;
  if (requiredProductFeatureFlag) {
    content = (
      <ProductFeatureFlagGuard
        flag={requiredProductFeatureFlag}
        requireEnabled={requireProductFeatureFlagEnabled}
        redirectTo={productFeatureFlagRedirectTo}
        mode="route"
      >
        {content}
      </ProductFeatureFlagGuard>
    );
  }
  if (featureFlag) {
    content = (
      <FeatureFlagGuard feature={featureFlag} mode="route">
        {content}
      </FeatureFlagGuard>
    );
  }
  if (requiredProduct) {
    // The optional `feature` prop is the rollout fallback used in non-dev
    // envs — see FeatureFlagGuard.js for the full semantics.
    content = (
      <FeatureFlagGuard
        product={requiredProduct}
        catalogOnly={requiredProductCatalogOnly}
        feature={requiredProductFallbackFeature}
        mode="route"
      >
        {content}
      </FeatureFlagGuard>
    );
  }

  // Cover both the legacy `featureFlag` gate and the new `requiredProduct`
  // gate so the GA Seasons whitelist bypass in `RequireAuth` keeps firing
  // for `additional_features.game_pass_pam` admins.
  const isGamePassRoute =
    featureFlag === "gamePass" ||
    requiredProduct === TRIVELTA_PRODUCTS.GAME_PASS;

  return (
    <RequireAuth
      requiredPermission={developmentMode ? undefined : requiredPermission}
      allowGamePassPamAccess={isGamePassRoute}
    >
      {content}
    </RequireAuth>
  );
}
